import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import {
  FilterPanelContainer,
  getChartColors,
  ItemListContainer,
  NoDataBox,
  PageContainer,
} from 'components/GlobalStyles/styles';
import SnackBar, { MessageType } from 'components/SnackBar';
import PeriodSelector from 'features/PeriodSelector';
import { dateFilterOptions } from 'pages/Teams/Teams';
import { useResourceUtilizationQuery } from 'services/busmanApi';
import { JOB_TYPE_LABELS } from 'utils/constants';

import { RootState } from '../../store';
import { TeamGridItem } from '../TeamOverview/styles';

import { EndGridItem, OverviewDetailBox, StartGridItem } from './styles';
import { ResourceDataProps } from './types';

const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      display: false,
    },
  },
};

const jobTypeMap: Record<string, string> = {
  Quoted: 'Quoted',
  'Time Billed': 'TimeBilled',
  Internal: 'Internal',
};

/**
 * Display team resource utilization details in a table and graph format,
 * allowing users to filter by date range and view individual employee breakdowns.
 *
 * Implements synchronized horizontal scrolling for the table header and body,
 * and provides navigation to employee-specific details.
 *
 * NAVIGATION VIEW: /teams/overview/:teamID
 */
const TeamOverviewDetail = () => {
  const theme = useTheme();
  const chartColours = getChartColors(theme);

  // Refs for synchronized scrolling
  const hasGoneOverBudget = useRef(false);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const totalScrollRef = useRef<HTMLDivElement>(null);
  const employeeScrollRefs = useRef<(HTMLDivElement | null)[]>([]);

  const navigate = useNavigate();
  const { teamID } = useParams();
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('All');
  const [message, setMessage] = useState<MessageType | null>(null);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [displayUnit, setDisplayUnit] = React.useState<'number' | 'percent'>('number');
  const { fromDateState, toDateState, selectedFilterState } = useSelector((state: RootState) => state.periodSelector);

  const [teamId, setTeamId] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [dateFilters, setDateFilters] = useState(selectedFilterState || dateFilterOptions[0]);

  // Scroll indicator states
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    fromDateState ? dayjs(fromDateState) : dayjs().subtract(1, 'day'),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(toDateState ? dayjs(toDateState) : dayjs());
  const formattedStartDate = fromDate ? fromDate.format('YYYY-MM-DD') : '';
  const formattedEndDate = toDate ? toDate.format('YYYY-MM-DD') : '';
  const handleBackClick = () => {
    navigate('/teams');
  };

  React.useEffect(() => {
    if (teamID) {
      setTeamId(teamID);
    }
  }, [teamID]);

  const {
    data: resourceData,
    isFetching: isResourceDataFetching,
    error,
  } = useResourceUtilizationQuery(
    {
      team_id: teamId || '',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    },
    {
      skip: !formattedStartDate || !formattedEndDate,
    },
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resources = resourceData?.resources ?? [];

  React.useEffect(() => {
    if (error && 'data' in error) {
      const errData = error.data as { errors?: string } | undefined;
      setMessage({ type: 'error', msg: `${errData?.errors ?? 'An error occurred'}` });
    }
  }, [error]);

  const jobNumbers: string[] = Array.from(
    new Set(
      resources.flatMap((resourceItem: ResourceDataProps) =>
        resourceItem.jobs
          .filter((job) => {
            if (jobTypeFilter === 'All') return true;
            return jobTypeMap[jobTypeFilter] === job.job_type;
          })
          .map((job) => job.job_number),
      ),
    ),
  );

  const { tableData, graphData } = useMemo(() => {
    const map: {
      [key: string]: {
        user_id: string;
        employee: string;
        job: {
          [job_number: string]: {
            hours: number;
            jobType: string;
            isOverBudget: boolean;
          };
        };
      };
    } = {};
    const tableDataTemp: {
      user_id: string;
      employee: string;
      job: {
        [job_number: string]: {
          hours: number;
          jobType: string;
          isOverBudget: boolean;
        };
      };
    }[] = [];

    const graphDataTemp: {
      employee: string;
      employeeName: string;
      [jobType: string]: number | string;
    }[] = [];

    resources.forEach((resourceItem: ResourceDataProps) => {
      const employeeName = `${resourceItem.firstname} ${resourceItem.lastname}`;
      const userId = resourceItem.user_id;
      const jobObj: { [job_number: string]: { hours: number; jobType: string; isOverBudget: boolean } } = {};
      const jobTypeHours: { [jobType: string]: number } = {};

      resourceItem.jobs.forEach((job) => {
        const type = job.job_type;
        jobObj[job.job_number] = {
          hours: job.job_hours,
          jobType: type,
          isOverBudget: job.is_overrun,
        };
        jobTypeHours[type] = (jobTypeHours[type] || 0) + job.job_hours;
        hasGoneOverBudget.current = hasGoneOverBudget.current || job.is_overrun;
      });

      const entry = {
        user_id: userId,
        employee: employeeName,
        job: jobObj,
      };

      map[userId] = entry;
      tableDataTemp.push(entry);
      graphDataTemp.push({
        employee: userId,
        employeeName,
        Quoted: jobTypeHours.Quoted || 0,
        TimeBilled: jobTypeHours.TimeBilled || 0,
        Internal: jobTypeHours.Internal || 0,
      });
    });

    return { tableData: tableDataTemp, graphData: graphDataTemp };
  }, [resources]);

  // Synchronized scrolling effect
  useEffect(() => {
    const headerEl = headerScrollRef.current;
    const totalEl = totalScrollRef.current;
    const employeeEls = employeeScrollRefs.current.filter((el) => el !== null) as HTMLDivElement[];

    if (!headerEl || !totalEl || employeeEls.length === 0) return;

    const allScrollableElements = [headerEl, totalEl, ...employeeEls];

    const syncScroll = (source: HTMLDivElement) => {
      return () => {
        allScrollableElements.forEach((target) => {
          if (target !== source) {
            target.scrollLeft = source.scrollLeft;
          }
        });
      };
    };

    const handlers = allScrollableElements.map((el) => {
      const handler = syncScroll(el);
      el.addEventListener('scroll', handler);
      return { element: el, handler };
    });

    return () => {
      handlers.forEach(({ element, handler }) => {
        element.removeEventListener('scroll', handler);
      });
    };
  }, [jobNumbers.length, tableData.length]);

  // Check scroll indicators
  const checkScrollIndicators = (element: HTMLDivElement) => {
    const canScrollLeftValue = element.scrollLeft > 0;
    const canScrollRightValue = element.scrollLeft < element.scrollWidth - element.clientWidth;
    setCanScrollLeft(canScrollLeftValue);
    setCanScrollRight(canScrollRightValue);
  };

  // Handle scroll indicator updates
  useEffect(() => {
    const headerEl = headerScrollRef.current;
    if (!headerEl) return;

    const handleScroll = () => checkScrollIndicators(headerEl);

    // Initial check
    checkScrollIndicators(headerEl);

    // Add scroll listener
    headerEl.addEventListener('scroll', handleScroll);

    return () => {
      headerEl.removeEventListener('scroll', handleScroll);
    };
  }, [jobNumbers.length]);

  const handleJobTypeFilter = (event: SelectChangeEvent) => {
    const selectedFilterValue = event.target.value as string;
    setJobTypeFilter(selectedFilterValue);
  };

  // Sidebar Component
  const aside = () => {
    return (
      <>
        <FilterPanelContainer>
          {!isMobileScreen && (
            <Box
              sx={{
                mb: 2,
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                gap: theme.spacing(1),
                alignItems: 'center',
              }}>
              <Box onClick={handleBackClick} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <ArrowBackIosIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ paddingTop: '4px' }}>
                Overall Team
              </Typography>
            </Box>
          )}

          <Box sx={{ paddingY: theme.spacing(1) }}>
            <Typography variant="h6" gutterBottom>
              Job Types
            </Typography>
            <Select
              size="small"
              value={jobTypeFilter}
              onChange={handleJobTypeFilter}
              sx={{
                width: '100%',
              }}>
              {[...JOB_TYPE_LABELS, 'All'].map((filter) => {
                return (
                  <MenuItem key={filter} value={filter}>
                    {filter}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
          <PeriodSelector
            selectedFilter={dateFilters}
            fromDate={fromDate}
            toDate={toDate}
            onFilterChange={setDateFilters}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </FilterPanelContainer>
      </>
    );
  };

  // Graph Data Preparation
  const overallBreakDown = useMemo(() => {
    return jobNumbers.reduce<{ [key: string]: number }>((acc, job) => {
      acc[job] = tableData.reduce((sum, item) => {
        const value = item.job[job]?.hours || 0;
        const num = typeof value === 'number' ? value : Number(value);
        return sum + (isNaN(num) ? 0 : num);
      }, 0);
      return acc;
    }, {});
  }, [jobNumbers, tableData]);

  const selectedEmployeeData = selectedEmployee ? graphData.find((item) => item.employee === selectedEmployee) : null;
  const overallGraphData = [
    graphData.reduce((acc, item) => acc + (item.Quoted ? Number(item.Quoted) : 0), 0),
    graphData.reduce((acc, item) => acc + (item.TimeBilled ? Number(item.TimeBilled) : 0), 0),
    graphData.reduce((acc, item) => acc + (item.Internal ? Number(item.Internal) : 0), 0),
  ];

  const selectedEmployeeGraphData = selectedEmployeeData
    ? [
        selectedEmployeeData.Quoted ? Number(selectedEmployeeData.Quoted) : 0,
        selectedEmployeeData.TimeBilled ? Number(selectedEmployeeData.TimeBilled) : 0,
        selectedEmployeeData.Internal ? Number(selectedEmployeeData.Internal) : 0,
      ]
    : overallGraphData;

  const filteredChartResult = selectedEmployeeGraphData
    .map((item, index) => ({
      item,
      label: JOB_TYPE_LABELS[index],
      color: chartColours[index % chartColours.length],
    }))
    .filter((item) => item.item !== 0);

  const chartData = {
    labels: filteredChartResult.map((entry) => entry.label),
    datasets: [
      {
        label: 'hours',
        data: filteredChartResult.map((e) => e.item),
        backgroundColor: filteredChartResult.map((e) => e.color),
      },
    ],
  };

  const navigateToDetails = (employeeId: string) => {
    navigate(`/teams/overview/${teamId}/${employeeId}`);
  };

  const renderGraphSection = () => {
    const totalValue = chartData.datasets[0].data.reduce((sum, val) => sum + val, 0);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <Box sx={{ height: '44vh', padding: theme.spacing(4) }}>
          <Doughnut data={chartData} options={chartOptions} />
        </Box>

        {chartData.labels.length > 0 && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginBottom: theme.spacing(2),
            }}>
            <ToggleButtonGroup
              color="primary"
              value={displayUnit}
              exclusive
              onChange={(_, newUnit) => newUnit && setDisplayUnit(newUnit)}
              aria-label="Display Unit"
              size="small">
              <ToggleButton value="number">Value</ToggleButton>
              <ToggleButton value="percent">% age</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            border: '2px solid',
            borderRadius: '4px',
            alignItems: 'center',
            padding: theme.spacing(2),
            paddingY: theme.spacing(2),
            marginTop: theme.spacing(2),
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
          }}>
          <Typography
            variant="h5"
            sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.primary, fontWeight: 700 }}>
            Total
          </Typography>
          <Typography variant="h5" sx={{ marginLeft: 'auto', color: theme.palette.text.primary, fontWeight: 700 }}>
            {displayUnit === 'number' ? totalValue : '100%'}
          </Typography>
        </Box>

        {chartData.labels.map((label, index) =>
          chartData.datasets[0].data[index] !== 0 ? (
            <Box
              key={index}
              sx={{
                width: '100%',
                display: 'flex',
                border: '1px solid',
                borderRadius: '4px',
                alignItems: 'center',
                padding: theme.spacing(2),
                paddingY: theme.spacing(2),
                marginTop: theme.spacing(2),
                borderColor: theme.palette.divider,
              }}>
              <Typography
                variant="h5"
                sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.primary }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '2px',
                    display: 'inline-block',
                    marginRight: theme.spacing(1),
                    backgroundColor: chartData.datasets[0].backgroundColor[index],
                  }}
                />
                {label}
              </Typography>
              <Typography
                variant="h5"
                sx={{ marginLeft: 'auto', color: theme.palette.text.primary, display: 'flex', alignItems: 'center' }}>
                {hasGoneOverBudget.current && label === JOB_TYPE_LABELS[0] && (
                  <Tooltip title="One or more job(s) has gone over-budget" arrow>
                    <WarningAmberIcon sx={{ color: theme.palette.error.main, marginRight: theme.spacing(1) }} />
                  </Tooltip>
                )}
                {displayUnit === 'percent'
                  ? `${((chartData.datasets[0].data[index] / totalValue) * 100).toFixed(2)}%`
                  : chartData.datasets[0].data[index]}
              </Typography>
            </Box>
          ) : null,
        )}
      </Box>
    );
  };

  return (
    <>
      <PageContainer>
        {aside()}
        {isResourceDataFetching ? (
          <NoDataBox>
            <Typography sx={{ padding: theme.spacing(2) }} variant="h6">
              Loading team data...
            </Typography>
          </NoDataBox>
        ) : resources.length === 0 || error ? (
          <NoDataBox>
            <Typography sx={{ padding: theme.spacing(2) }} variant="h6">
              No data available for the selected date range.
            </Typography>
          </NoDataBox>
        ) : (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: theme.spacing(1),
              flexDirection: { xs: 'column', md: 'row' },
            }}>
            {/* Table Component */}
            <OverviewDetailBox onClick={() => setSelectedEmployee(null)} data-testid={'overview-detail-table'}>
              {/* Team Overview Header */}
              <ItemListContainer data-testid="scrollable-table">
                <Grid container display="flex" flexDirection="row" sx={{ position: 'relative' }}>
                  <StartGridItem item>
                    <Typography variant="body2" sx={{ color: '#9E9E9E' }}>
                      Team Name
                    </Typography>
                  </StartGridItem>

                  {canScrollLeft && (
                    <ChevronLeftIcon
                      sx={{
                        left: 0,
                        zIndex: 2,
                        fontSize: '18px',
                        color: '#9E9E9E',
                      }}
                    />
                  )}

                  <Box
                    ref={headerScrollRef}
                    data-testid="job-number-header"
                    sx={{
                      flex: 1,
                      display: 'flex',
                      overflowX: 'auto',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}>
                    {jobNumbers.map((job, index) => (
                      <Grid
                        item
                        key={index}
                        sx={{
                          minWidth: 90,
                          padding: '2px',
                          display: 'flex',
                          color: '#9E9E9E',
                          justifyContent: 'center',
                        }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: tableData.some((tableItem) => tableItem.job[job]?.isOverBudget) ? 'red' : '',
                          }}>
                          {job}
                        </Typography>
                      </Grid>
                    ))}
                  </Box>

                  {/* Right scroll indicator */}
                  {canScrollRight && (
                    <ChevronRightIcon
                      sx={{
                        right: 0,
                        zIndex: 3,
                        fontSize: '18px',
                        color: '#9E9E9E',
                      }}
                    />
                  )}

                  <EndGridItem item md={2}>
                    <Typography variant="body2" sx={{ color: '#9E9E9E' }}>
                      Total
                    </Typography>
                  </EndGridItem>
                </Grid>

                {/* Team Overview Body */}
                {tableData.map((employee, employeeIndex) => (
                  <TeamGridItem
                    container
                    key={employee.user_id}
                    data-testid={`employee-item-${employeeIndex}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedEmployee(employee.user_id.toString());
                    }}>
                    <StartGridItem item>
                      <Typography variant="body1">{employee.employee}</Typography>
                    </StartGridItem>

                    <Box
                      ref={(el: HTMLDivElement | null) => (employeeScrollRefs.current[employeeIndex] = el)}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                      }}>
                      {jobNumbers.map((job) => (
                        <Grid
                          item
                          key={employee.user_id + job}
                          sx={{
                            minWidth: 90,
                            padding: '2px',
                            display: 'flex',
                            justifyContent: 'center',
                          }}>
                          <Tooltip
                            title={`${employee.job[job]?.jobType || 'No data'} - ${
                              employee.job[job]?.hours || 0
                            } hours ${employee.job[job]?.isOverBudget ? '(Over Budget)' : ''}`}
                            arrow>
                            <Typography variant="body1" sx={{ color: employee.job[job]?.isOverBudget ? 'red' : '' }}>
                              {employee.job[job]?.hours || 0}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      ))}
                    </Box>

                    <EndGridItem item md={2}>
                      <Typography variant="body1">
                        {jobNumbers.reduce((total, job) => total + Number(employee.job[job]?.hours || 0), 0)}
                      </Typography>
                    </EndGridItem>
                  </TeamGridItem>
                ))}
              </ItemListContainer>

              {/* Total table */}
              <Box sx={{ padding: theme.spacing(2) }}>
                <TeamGridItem container>
                  <StartGridItem item>
                    <Typography variant="body1">Total</Typography>
                  </StartGridItem>

                  <Box
                    ref={totalScrollRef}
                    sx={{ display: 'flex', overflowX: 'auto', flex: 1, scrollbarWidth: 'none', textAlign: 'center' }}>
                    {jobNumbers.map((label, index) => (
                      <Grid item key={index} md={2} sx={{ minWidth: 90 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            color: tableData.some((tableItem) => tableItem.job[label]?.isOverBudget) ? 'red' : '',
                          }}>
                          {overallBreakDown[label]}
                        </Typography>
                      </Grid>
                    ))}
                  </Box>

                  <EndGridItem item md={2}>
                    <Typography variant="body1">
                      {Object.values(overallBreakDown).reduce((sum, v) => sum + v, 0)}
                    </Typography>
                  </EndGridItem>
                </TeamGridItem>
              </Box>
            </OverviewDetailBox>

            <OverviewDetailBox data-testid={'overview-detail-graph'}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: theme.spacing(1),
                  justifyContent: 'space-between',
                }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ color: theme.palette.text.primary, paddingTop: theme.spacing(1) }}>
                  {selectedEmployeeData ? `${selectedEmployeeData.employeeName} Breakdown` : 'Overall Team Breakdown'}
                </Typography>
                {selectedEmployee && (
                  <CustomButton
                    variant="contained_grey"
                    text="View Detail"
                    onClick={() => navigateToDetails(selectedEmployee.toString())}
                    width="medium"
                    customSx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                    endIcon={<ArrowForwardIosIcon />}
                  />
                )}
              </Box>
              <Divider />

              {/* Graph Section */}
              {renderGraphSection()}
            </OverviewDetailBox>
          </Box>
        )}
      </PageContainer>
      <SnackBar message={message} onClose={() => setMessage(null)} />
    </>
  );
};

export default TeamOverviewDetail;
